import { groupBy, isFunction } from "lodash";
import { useState } from "react";

export const useSearchOptions = <Option = unknown>(
  options: Option[] | readonly Option[],
  filterBy: (options: Option[] | readonly Option[], search: string) => Option[],
  groupByValue?: (option: Option) => string | undefined,
  onSearchChange?: (value: string) => void,
  // Options render without virtualization, so an unbounded list (e.g. a community
  // with thousands of tags) can freeze the tab on open. When set, caps what
  // gets rendered and reports hasMoreResults so callers can prompt the user
  // to keep typing instead of paying to mount all of them. Opt-in: leave
  // unset for callers that don't need it.
  maxVisibleOptions?: number,
) => {
  const [search, setSearch] = useState("");
  const clearSearch = () => setSearch("");

  const handleSearchChange = (value: string) => {
    setSearch(value);
    onSearchChange?.(value);
  };

  const filteredResults = filterBy(options, search);
  const searchResults = maxVisibleOptions
    ? filteredResults.slice(0, maxVisibleOptions)
    : filteredResults;
  const hasMoreResults = Boolean(
    maxVisibleOptions && filteredResults.length > maxVisibleOptions,
  );

  const hasNoResults =
    (searchResults.length === 0 && search !== "") || options.length === 0;

  const shouldGroup = isFunction(groupByValue) && !hasNoResults;

  const groupedSearchResults = shouldGroup
    ? groupBy(searchResults, groupByValue)
    : null;

  const hasGroups = Boolean(
    groupedSearchResults && Object.keys(groupedSearchResults).length > 0,
  );

  return {
    search,
    clearSearch,
    handleSearchChange,
    searchResults,
    hasNoResults,
    hasMoreResults,
    groupedSearchResults,
    hasGroups,
  };
};
